import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions, MODULES } from '../context/PermissionContext';
import { useFirmendaten } from '../hooks/useFirmendaten';
import {
  FileText,
  Calculator,
  Package,
  Settings,
  LogOut,
  ChevronRight,
  Clock,
  CalendarClock,
  CalendarX,
  Users,
  Bot,
  FolderKanban,
  Type
} from '../icons/wp-icons';

const menuItems = [
  {
    category: 'Dokumente',
    items: [
      { path: '/kostenvoranschlaege', icon: Calculator, label: 'Kostenvoranschläge', modul: MODULES.KOSTENVORANSCHLAEGE },
      { path: '/rechnungen', icon: FileText, label: 'Rechnungen', modul: MODULES.RECHNUNGEN },
      { path: '/projekte', icon: FolderKanban, label: 'Projekte', modul: MODULES.PROJEKTE },
      { path: '/katalog', icon: Package, label: 'Katalog & Vorlagen', modul: MODULES.KATALOG }
    ]
  },
  {
    category: 'Kalender',
    items: [
      { path: '/kalender/geschaeftszeiten', icon: Clock, label: 'Geschäftszeiten', modul: MODULES.TERMINE },
      { path: '/kalender/blockierte-zeiten', icon: CalendarX, label: 'Blockierte Zeiten', modul: MODULES.TERMINE }
    ]
  },
  {
    category: 'System',
    items: [
      { path: '/mitarbeiter', icon: Users, label: 'Mitarbeiter', adminOnly: true },
      { path: '/ki-zentrale', icon: Bot, label: 'KI Zentrale', modul: MODULES.EINSTELLUNGEN },
      { path: '/einstellungen', icon: Settings, label: 'Einstellungen', modul: MODULES.EINSTELLUNGEN }
    ]
  }
];

export default function MobileMenu() {
  const { user, logout } = useAuth();
  const { canView, isAdmin, loading } = usePermissions();
  const { firmendaten } = useFirmendaten();
  const firmenname = firmendaten?.firmenname || '';

  const handleLogout = async () => {
    await logout();
  };

  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const HEYKIKI_ALLOWED_PATHS = ['/dashboard', '/anfragen', '/kunden', '/kalender', '/kalender/geschaeftszeiten', '/kalender/blockierte-zeiten', '/ki-zentrale', '/kostenvoranschlaege', '/rechnungen', '/einstellungen', '/katalog', '/projekte', '/mitarbeiter'];

  // Filter menu items based on permissions
  // Wichtig: Während loading alle Items anzeigen, um Flackern zu vermeiden
  const filteredMenuItems = menuItems.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (isHeykikiOrg && !HEYKIKI_ALLOWED_PATHS.includes(item.path)) return false;
      if (item.adminOnly) return isAdmin;
      if (loading) return true; // Während Berechtigungen laden, alle Items anzeigen
      if (item.modul) return canView(item.modul);
      return true;
    }).map(item => item.path === '/ki-zentrale' && isHeykikiOrg ? { ...item, label: 'KiKi Zentrale' } : item)
  })).filter(section => section.items.length > 0);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="mobile-header">
        <h1>Menü</h1>
      </div>

      {/* User Info */}
      <div className="bg-white p-4 mx-4 mt-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-lg">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-semibold text-text">{user?.name || 'Benutzer'}</p>
            <p className="text-sm text-text-muted">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Menu Sections */}
      {filteredMenuItems.map((section) => {
        const visibleItems = section.items;
        return (
          <div key={section.category} className="mt-6">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider px-4 mb-2">
              {section.category}
            </p>
            <div className="bg-white mx-4 rounded-xl shadow-sm overflow-hidden">
              {visibleItems.map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                    index !== visibleItems.length - 1 ? 'border-b border-border' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-text-muted" />
                    <span className="text-text">{item.label}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-muted" />
                </Link>
              ))}
            </div>
          </div>
        );
      })}

      {/* Logout Button */}
      <div className="mt-8 px-4">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 p-4 bg-red-50 text-danger rounded-xl font-medium hover:bg-red-100 active:bg-red-200 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Abmelden</span>
        </button>
      </div>

      {/* Version Info */}
      <div className="mt-8 text-center text-sm text-text-muted">
        <p>{isHeykikiOrg ? (firmenname || '') : `${firmenname ? `${firmenname} - ` : ''}WerkPilot`}</p>
        <p className="text-xs mt-1">Version 1.0.0</p>
      </div>
    </div>
  );
}
